import { bootedIdentity, injectLoader, removeWidgetNodes } from './dom';
import { RespondSettings } from './types';

// Mints the short-lived ticket that identifies this visitor to the shell.
export type MintTicket = () => Promise<string | null>;

// The identity a sync is currently booting, or null when none is in flight.
// Minting is async, so without this a second sync (a fast logout after login,
// say) could interleave and leave the widget bound to the wrong user.
let inFlight: string | null = null;

export function destroyWidget(): void {
  // Cancels any sync still waiting on its ticket: it checks this before
  // injecting, so an explicit destroy wins.
  inFlight = null;
  window.__respond_settings = {};
  removeWidgetNodes();
}

// Bind the widget to `settings`, re-creating it only when the identity actually
// changed — re-creating discards any conversation in progress, and the auth
// effect that drives this can re-run for unrelated reasons.
export async function syncIdentity(
  settings: RespondSettings,
  src: string | null,
  mintTicket: MintTicket,
): Promise<void> {
  if (!src) {
    // No loader configured: the widget is disabled for this build.
    return;
  }

  const next = settings.identifier ?? '';
  const current = inFlight ?? bootedIdentity();

  if (current === next) {
    return;
  }

  inFlight = next;

  try {
    // Anonymous visitors get no ticket: there is no anonymous ticket to mint,
    // and the shell deliberately clears respond.io's storage when it renders
    // unidentified, so the previous user's transcript cannot be read by the
    // next person on this browser.
    //
    // Minted here rather than earlier because it lives only ~60s and respond.io
    // renders its chat iframe at page load, not when the chat is opened.
    const ticket = next ? await mintTicket() : null;

    // A later sync (or an explicit destroy) superseded this one while we waited.
    if (inFlight !== next) {
      return;
    }

    if (bootedIdentity() !== null) {
      removeWidgetNodes();
    }

    // Must precede injection: appending an async script only starts the fetch,
    // and the loader reads this global when the iframe reports itself loaded.
    // A null ticket is left off entirely — the shell then renders anonymously,
    // which is the documented fail-closed behaviour.
    window.__respond_settings = ticket ? { ...settings, ticket } : settings;
    injectLoader(src, next);
  } finally {
    if (inFlight === next) {
      inFlight = null;
    }
  }
}
